import React, { useEffect, useRef, useState } from "react";
import {
    FiAlertCircle,
    FiCheckCircle,
} from "react-icons/fi";

interface EmailAvailabilityFieldProps {
    emailAddress: string;
    setEmailAddress: (value: string) => void;
    getEmailChecking: (email: string) => Promise<any>;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
}

const EmailAvailabilityField = ({
    emailAddress,
    setEmailAddress,
    getEmailChecking,
    label = "Email Address",
    placeholder = "Enter email address",
    required = true,
    disabled = false,
    readOnly = false,
}: EmailAvailabilityFieldProps) => {
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isEmailExists, setIsEmailExists] = useState<boolean | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const hasUserChangedEmail = useRef(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!hasUserChangedEmail.current) {
            return;
        }

        if (!emailAddress?.trim()) {
            setIsEmailValid(true);
            setIsEmailExists(null);
            setIsChecking(false);
            return;
        }

        const timer = setTimeout(() => {
            const email = emailAddress.trim();

            const isValid =
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[A-Za-z]{2,}$/.test(email);

            setIsEmailValid(isValid);

            if (!isValid) {
                setIsEmailExists(null);
                return;
            }

            checkEmailAvailability(email);
        }, 1000);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailAddress]);

    const checkEmailAvailability = async (email: string) => {
        try {
            setIsChecking(true);

            const response = await getEmailChecking(email);

            const exists =
                response?.data?.exists ??
                response?.exists ??
                false;

            setIsEmailExists(exists);

            if (inputRef.current) {
                inputRef.current.setCustomValidity(
                    exists ? "Email already exists" : ""
                );
            }

            if (!exists) {
                setTimeout(() => {
                    setIsEmailExists(null);
                }, 3000);
            }
        } catch (error) {
            console.error("Error checking email availability:", error);

            setIsEmailExists(null);

            if (inputRef.current) {
                inputRef.current.setCustomValidity("");
            }
        } finally {
            setIsChecking(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        hasUserChangedEmail.current = true;

        setEmailAddress(value);
        setIsEmailValid(true);
        setIsEmailExists(null);

        e.target.setCustomValidity("");
    };

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {label}{" "}
                {required && <span className="text-red-500">*</span>}
            </label>

            <input
                ref={inputRef}
                type="email"
                value={emailAddress}
                onChange={handleChange}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
            />

            {!isEmailValid && (
                <div className="flex items-center text-xs text-red-500 mt-1">
                    <FiAlertCircle className="mr-1" size={16} />
                    <span>Please enter a valid email address</span>
                </div>
            )}

            {isChecking && isEmailValid && (
                <p className="text-xs text-gray-500 mt-1">
                    Checking email...
                </p>
            )}

            {isEmailValid && isEmailExists === false && !isChecking && (
                <div className="flex items-center text-xs text-green-600 mt-1">
                    <FiCheckCircle className="mr-1" size={16} />
                    <span>Email is available</span>
                </div>
            )}

            {isEmailValid && isEmailExists === true && !isChecking && (
                <div className="flex items-center text-xs text-red-500 mt-1">
                    <FiAlertCircle className="mr-1" size={16} />
                    <span>Email already exists</span>
                </div>
            )}
        </div>
    );
};

export default EmailAvailabilityField;